<template>
  <div>
    <AddPersonOrStep v-model="isAddPersonOrStepDialogVisible"
                     @showAddStepDialog="showAddStepDialog()" />

    <AddEditStepDialog :method="method"
                       :selected-step-id="selectedStepId"
                       v-model="isAddEditStepDialogVisible"
                       @getSteps="getSequenceStepsService()"/>

    <AddUserToSequence
      :selected-sequence-id="$route.params.sequenceId"
      :selected-user-id="selectedUserId"
      v-model="isChangeUserFromSequenceDialogVisible"
      @clearSelectedUserId="clearSelectedUserId()" />

    <DeleteItemDialog :title="$t('general.deleteUserFromSequenceConfirmation')"
                      v-model="isDeleteUserFromSequenceDialogVisible"
                      @onDeleteItem="deleteUserFromSequence()" />

    <EmailComposer @clear="selectedPersonEmail = ''"
                   :selectedPersonEmail="selectedPersonEmail"
                   v-model="showEmailComposer">
    </EmailComposer>

    <template v-if="loadingSequence">
      <LoadingComponent :message="$t('general.loadingSequence')" />
    </template>

    <template v-else>
      <SequenceToolbar :sequence="selectedSequence"
                       :viewType="viewType"
                       @changeSequenceViewType="changeSequenceViewType($event)"
                       @showAddPersonOrStepDialog="showAddPersonOrStepDialog()" />

      <template v-if="viewType === 'cards'">
        <LeadsCardsView :pagination="pagination"
                        @changeCanGetPeopleOnPaginationStatus="canGetPeopleOnPaginationChange = true"
                        @changeUserFromSequence="showChangeUserFromSequenceDialog"
                        @deleteUserFromSequence="openDeleteUserFromSequenceDialog"
                        @getSequencePeople="getSequencePeople()"
                        @onPaginationChange="onPaginationChange"
                        @openEmailComposer="openEmailComposer($event)">
          <template v-slot:steps-row>
            <StepsRow @showAddStepDialog="showAddStepDialog()"
                      @showUpdateStepDialog="showUpdateStepDialog($event)" />
          </template>
        </LeadsCardsView>
      </template>

      <template v-else-if="viewType === 'list'">
        <LeadsListView :pagination="pagination"
                       @changeCanGetPeopleOnPaginationStatus="canGetPeopleOnPaginationChange = true"
                       @changeUserFromSequence="showChangeUserFromSequenceDialog"
                       @deleteUserFromSequence="openDeleteUserFromSequenceDialog"
                       @getSequencePeople="getSequencePeople()"
                       @onPaginationChange="onPaginationChange"
                       @openEmailComposer="openEmailComposer($event)">
          <template v-slot:steps-row>
            <StepsRow :allow-overflow="true"
                      @showAddStepDialog="showAddStepDialog()"
                      @showUpdateStepDialog="showUpdateStepDialog($event)" />
          </template>
        </LeadsListView>
      </template>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import AddEditStepDialog from '@/pages/App/Sequences/Steps/dialogs/AddEditStep'
import AddPersonOrStep from '@/pages/App/Sequences/dialogs/AddPersonOrStep'
import DeleteItemDialog from '@/components/ui/DeleteItemDialog'
import EmailComposer from '@/components/widgets/EmailComposer.vue'
import LeadsCardsView from '@/pages/App/Sequences/Leads/LeadsCardsView'
import LeadsListView from '@/pages/App/Sequences/Leads/LeadsListView'
import LoadingComponent from '@/components/ui/LoadingComponent'
import SequenceToolbar from '@/pages/App/Sequences/SequenceToolbar'
import StepsRow from '@/pages/App/Sequences/Steps/StepsRow'
import AddUserToSequence from '@/pages/App/People/dialogs/AddUserToSequence'
export default {
  components: {
    AddUserToSequence,
    AddPersonOrStep,
    AddEditStepDialog,
    DeleteItemDialog,
    EmailComposer,
    LeadsCardsView,
    LeadsListView,
    LoadingComponent,
    SequenceToolbar,
    StepsRow
  },
  computed: {
    ...mapState({
      selectedSequence: state => state.sequences.selectedSequence
    })
  },
  created () {
    this.getSequence()
  },
  data () {
    return {
      loadingSequence: false,
      isAddPersonDialogVisible: false,
      isAddEditStepDialogVisible: false,
      isAddPersonOrStepDialogVisible: false,
      isChangeUserFromSequenceDialogVisible: false,
      isDeleteUserFromSequenceDialogVisible: false,
      method: 'create',
      pagination: {
        groupBy: [],
        groupDesc: [],
        itemsPerPage: 50,
        multiSort: false,
        page: 1,
        sortBy: [],
        sortDesc: []
      },
      selectedPersonEmail: '',
      selectedStepId: '',
      selectedUserId: '',
      showEmailComposer: false,
      viewType: 'cards'
    }
  },
  methods: {
    changeSequenceViewType (viewType) {
      this.viewType = viewType
    },
    clearSelectedUserId () {
      this.selectedUserId = ''
    },
    async deleteUserFromSequence () {
      try {
        await this.$store.dispatch('deleteUserFromSequence', {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId,
          userId: this.selectedUserId
        })
        notifications.success(this.$t('general.userSuccessfullyDeletedFromSequence'))
        this.clearSelectedUserId()
        this.loadingSequence = true
        await this.getSequencePeople()
        this.loadingSequence = false
      } catch (e) {
        this.loadingSequence = false
        notifications.error(e)
      }
    },
    async getSequence () {
      try {
        this.loadingSequence = true
        await Promise.all([
          this.getSequencePeople(),
          this.getSequenceService(),
          this.getSequenceStepsService()
        ])
        this.loadingSequence = false
      } catch (e) {
        this.loadingSequence = false
        notifications.error(e)
      }
    },
    async getSequencePeople () {
      try {
        await this.$store.dispatch('getSequencePeople', {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId,
          pagination: this.pagination
        })
      } catch (e) {
        notifications.error(e)
      }
    },
    async getSequenceService () {
      await this.$store.dispatch('getSequence',
        {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId
        }
      )
    },
    async getSequenceStepsService () {
      await this.$store.dispatch('getSequenceSteps',
        {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId
        }
      )
    },
    openEmailComposer (email) {
      this.selectedPersonEmail = email
      this.showEmailComposer = true
    },
    onPaginationChange (pagination) {
      this.pagination = pagination
      this.getSequencePeople()
    },
    openDeleteUserFromSequenceDialog (userId) {
      this.selectedUserId = userId
      this.isDeleteUserFromSequenceDialogVisible = true
    },
    showAddPersonOrStepDialog () {
      this.isAddPersonOrStepDialogVisible = true
    },
    showAddStepDialog () {
      this.method = 'create'
      this.selectedStepId = ''
      this.isAddEditStepDialogVisible = true
    },
    showChangeUserFromSequenceDialog (userId) {
      this.selectedUserId = userId
      this.isChangeUserFromSequenceDialogVisible = true
    },
    showUpdateStepDialog (stepId) {
      this.method = 'edit'
      this.selectedStepId = stepId
      this.isAddEditStepDialogVisible = true
    }
  }
}
</script>
